import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  FormControl,
  Stack,
  TextField,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Tooltip,
  Dialog,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Chart from "../Chart";
import AccService from "../../services/AccService";
import { useDispatch } from "react-redux";
import { snackbarActions } from "../../Store/snackbar";
import AdminService from "../../services/AdminService";
import dayjs from "dayjs";
import SiteBarChart from "../BarChart";
import {
  DataThresholdingOutlined,
  SubjectOutlined,
  SwapHorizontalCircleOutlined,
  TransitEnterexitOutlined,
} from "@mui/icons-material";
import ProjectSelect from "./ProjectSelect";

// Styles
const flexBTW = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
};
const tileStyle = {
  p: 1,
  minWidth: "250px",
  borderRadius: "10px",
  height: "90px",
  color: "#fff",
};
const chartContainer = {
  flex: 10,
  display: "flex",
  flexDirection: "column",
  gap: "16px",
};
const chartTiles = {
  flex: 6,
  borderRadius: "10px",
  width: "420px",
  height: "230px",
  color: "#000",
};

const Dashboard = () => {
  const [clientState, setClientState] = useState("");
  const [projectState, setProjectState] = useState([]);
  const [siteState, setSiteState] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [clientOptions, setClientOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);
  const [dashboardData, setDashboardData] = useState({});
  const [subjectMatrix, setSubjectMatrix] = useState("");
  const [loading, setLoading] = useState(false);
  const [defaultProject, setDefaultProject] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const userProfile = JSON.parse(sessionStorage.getItem("userProfile"));
  const [filtersChanged, setFiltersChanged] = useState(false);

  // Store initial filter values for comparison
  const [initialFilters, setInitialFilters] = useState({
    client: "",
    project: [],
    startDate: null,
    endDate: null,
  });

  const dispatch = useDispatch();

  const commonDispatch = (type, message) => {
    dispatch(
      snackbarActions.showNotification({
        snackbarOpen: true,
        snackbarType: type,
        snackbarMessage: message,
      })
    );
  };

  // Handlers
  const handleStartDateChange = (value) => {
    setStartDate(value ? dayjs(value).format("YYYY-MM-DD 00:00:00") : null);
  };

  const handleEndDateChange = (value) => {
    if (startDate && value && value.isBefore(dayjs(startDate), "day")) {
      commonDispatch("warning", "End Date cannot be before Start Date.");
      setEndDate(null);
      return;
    }
    setEndDate(value ? dayjs(value).format("YYYY-MM-DD 00:00:00") : null);
  };

  const handleClientChange = (event, newValue) => {
    setClientState(newValue);
    setProjectState([]);
    if (newValue) {
      getProjectList(newValue);
    } else {
      setProjectOptions([]);
    }
  };

  const clearFilter = () => {
    if (clientState && projectState && startDate && endDate) {
      setStartDate(dashboardData?.start_date);
      setEndDate(dashboardData?.end_date);
      setClientState(dashboardData?.client);
      setDefaultProject(
        Array.isArray(dashboardData?.project) && dashboardData?.project.length
          ? dashboardData?.project
          : dashboardData?.default_projects || []
      );
      setSiteState("");
      setProjectState(
        Array.isArray(defaultProject) && defaultProject.length
          ? defaultProject
          : []
      );
      getDefaultData();
      getDefaultSubject();
    }
  };

  const getDefaultData = () => {
    setLoading(true);
    AdminService.getDashboardData()
      .then((resp) => {
        setDashboardData(resp.data);
        setStartDate(resp.data.start_date);
        setEndDate(resp.data.end_date);
        setClientState(resp.data.client);
        setProjectState(
          Array.isArray(resp.data.project) && resp.data.project.length
            ? resp.data.project
            : resp.data.default_projects || []
        );
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const getDefaultSubject = () => {
    AdminService.getDefaultSubjectMetricsData().then((resp) => {
      setSubjectMatrix(resp.data);
    });
  };

  const getFilterDefaultData = () => {
    setLoading(true);
    AdminService.getDashboardFilterdData(
      clientState,
      projectState,
      startDate,
      endDate
    )
      .then((resp) => {
        setDashboardData(resp.data);
        setLoading(false);
        commonDispatch("success", "Filtered Data Successfully updated!");
      })
      .catch(() => setLoading(false));
  };

  const getFilterSubjectData = () => {
    AdminService.getSubjectFilterdData(
      clientState,
      projectState,
      startDate,
      endDate
    ).then((resp) => {
      setSubjectMatrix(resp.data);
    });
  };

  const getAllFilterData = () => {
    if (clientState && projectState && startDate && endDate) {
      getFilterDefaultData();
      getFilterSubjectData();
      setDefaultProject(
        Array.isArray(dashboardData?.project) && dashboardData?.project.length
          ? dashboardData?.project
          : dashboardData?.default_projects || []
      );
    } else {
      commonDispatch(
        "warning",
        "Provide all parameters to filter the Dashboard Data"
      );
    }
  };

  const getProjectList = (clientName) => {
    if (clientName) {
      AccService.getClientProjects(clientName)
        .then((resp) => setProjectOptions(resp.data))
        .catch(() => setProjectOptions([]));
    }
  };

  const handleSetDefault = () => {
    const payload = {
      username: userProfile.username,
      user_type: userProfile.user_type,
      default_client_projects: {
        [clientState]: projectState,
      },
    };
    AdminService.setDefault(payload)
      .then((resp) => {
        commonDispatch("success", resp.data);
        setShowConfirmModal(false);
        getDefaultData();
        getDefaultSubject();
      })
      .catch((err) => {
        commonDispatch(
          "warning",
          err?.response?.data?.detail || "Action failed"
        );
        setShowConfirmModal(false);
      });
  };

  // Effects
  useEffect(() => {
    AccService.getAllClients("").then((resp) => setClientOptions(resp.data));
  }, []);

  useEffect(() => {
    getProjectList(dashboardData?.client);
  }, [dashboardData.client]);

  useEffect(() => {
    getDefaultData();
    getDefaultSubject();
  }, []);

  useEffect(() => {
    if (
      Array.isArray(dashboardData.site_counts) &&
      dashboardData.site_counts.length > 0
    ) {
      setSiteState(dashboardData.site_counts[0].site);
    }
  }, [dashboardData.site_counts]);

  useEffect(() => {
    if (Object.keys(dashboardData).length > 0) {
      setInitialFilters({
        client: dashboardData?.client || "",
        project:
          Array.isArray(dashboardData?.project) && dashboardData?.project.length
            ? dashboardData.project
            : Array.isArray(dashboardData?.default_projects)
            ? dashboardData.default_projects
            : [],
        startDate: dashboardData?.start_date || null,
        endDate: dashboardData?.end_date || null,
      });
    }
  }, [dashboardData]);

  useEffect(() => {
    const clientChanged = clientState !== initialFilters.client;
    const projectChanged =
      JSON.stringify(projectState) !== JSON.stringify(initialFilters.project);
    const startChanged = startDate !== initialFilters.startDate;
    const endChanged = endDate !== initialFilters.endDate;
    setFiltersChanged(
      clientChanged || projectChanged || startChanged || endChanged
    );
  }, [clientState, projectState, startDate, endDate, initialFilters]);

  // Chart configs
  const chartConfigs = [
    {
      title: "Total Samples By Status",
      data: Array.isArray(dashboardData.status_counts)
        ? [...dashboardData.status_counts]
            .sort((a, b) => b.sample_count - a.sample_count)
            .map((item) => ({
              label: item.status,
              value: item.sample_count,
            }))
        : [],
      colors: [
        "#E7314A",
        "#FFB510",
        "#5AC1EF",
        "#632584",
        "#FFB510",
        "#FFAEBC",
        "#81B622",
        "#05445E",
      ],
    },
    {
      title: "Parent and Aliquot Ratio",
      data: [
        { label: "WIth Aliquots", value: dashboardData?.samples_received },
        { label: "WIthout Aliquots", value: dashboardData?.aliquot_samples },
      ],
      colors: ["#E7314A", "#FFB510"],
    },
    {
      title: "Sample Deviations",
      data: [
        {
          label: "With Deviation",
          value: dashboardData?.samples_with_deviation,
        },
        {
          label: "Without Deviation",
          value: dashboardData?.samples_without_deviation,
        },
      ],
      colors: ["#E7314A", "#FFB510"],
    },
  ];

  return (
    <Box sx={{ p: 0, m: 0, position: "relative" }}>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(255,255,255,0.7)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={80} color="primary" />
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Box>
          <Typography variant="h4">Dashboard</Typography>
        </Box>

        <Box>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Set default selected client and project(s)">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showConfirmModal}
                    name="Set Default"
                    onChange={() => setShowConfirmModal(true)}
                  />
                }
                label="Set Default"
                disabled={!projectState.length}
              />
            </Tooltip>

            {/* Client */}
            <FormControl
              size="small"
              sx={{ minWidth: "145px", maxWidth: "145px" }}
            >
              <Autocomplete
                id="free-solo-demo"
                freeSolo
                size="small"
                margin="dense"
                options={clientOptions}
                onChange={handleClientChange}
                renderInput={(params) => (
                  <TextField
                    margin="dense"
                    {...params}
                    id="standard-multiline-flexible"
                    size="small"
                    type="text"
                    name={"Client"}
                    label={"Client"}
                    sx={{ margin: "0px" }}
                  />
                )}
                value={
                  dashboardData?.client ? dashboardData?.client : clientState
                }
              />
            </FormControl>

            {/* Project */}
            <ProjectSelect
              projectOptions={projectOptions}
              projectState={projectState}
              setProjectState={setProjectState}
              disabled={!clientState}
              dashboardData={dashboardData}
              minWidth="150px"
            />

            {/* Start Date */}
            <Box sx={{ minWidth: "145px", maxWidth: "145px" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={handleStartDateChange}
                  disableFuture
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      inputProps={{
                        ...props.inputProps,
                        readOnly: true,
                      }}
                      size="small"
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>

            {/* End Date */}
            <Box sx={{ minWidth: "145px", maxWidth: "145px" }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={handleEndDateChange}
                  disableFuture
                  disabled={!startDate}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      inputProps={{
                        ...props.inputProps,
                        readOnly: true,
                      }}
                      size="small"
                    />
                  )}
                />
              </LocalizationProvider>
            </Box>

            <Button
              variant="contained"
              onClick={getAllFilterData}
              sx={{ width: "65px" }}
              disabled={!filtersChanged}
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              onClick={clearFilter}
              sx={{ width: "65px" }}
              disabled={!filtersChanged}
            >
              Reset
            </Button>
          </Stack>
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={0.5}>
          {/* Count titles */}
          <Box
            sx={{
              flex: 2,
              pr: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: "16px",
            }}
          >
            <Box sx={tileStyle} style={{ backgroundColor: "#632584" }}>
              <Typography variant="h6" sx={{ marginBottom: "16px" }}>
                Samples Received
              </Typography>
              <Box sx={flexBTW}>
                {dashboardData?.samples_received ? (
                  <Typography variant="h4">
                    {dashboardData?.samples_received}
                  </Typography>
                ) : (
                  <Typography variant="caption">No Record</Typography>
                )}
                <TransitEnterexitOutlined fontSize="large" />
              </Box>
            </Box>
            <Box sx={tileStyle} style={{ backgroundColor: "#5AC1EF" }}>
              <Typography variant="h6" sx={{ marginBottom: "16px" }}>
                Samples on Hold
              </Typography>
              <Box sx={flexBTW}>
                {dashboardData?.samples_onhold ? (
                  <Typography variant="h4">
                    {dashboardData?.samples_onhold}
                  </Typography>
                ) : (
                  <Typography variant="caption">No Record</Typography>
                )}
                <DataThresholdingOutlined fontSize="large" />
              </Box>
            </Box>
            <Box sx={tileStyle} style={{ backgroundColor: "#FFB510" }}>
              <Typography variant="h6" sx={{ marginBottom: "16px" }}>
                Subject Received
              </Typography>
              <Box sx={flexBTW}>
                {subjectMatrix?.subject_received ? (
                  <Typography variant="h4">
                    {subjectMatrix?.subject_received}
                  </Typography>
                ) : (
                  <Typography variant="caption">No Record</Typography>
                )}
                <SubjectOutlined fontSize="large" />
              </Box>
            </Box>
            <Box sx={tileStyle} style={{ backgroundColor: "#E7314A" }}>
              <Typography variant="h6" sx={{ marginBottom: "16px" }}>
                Aliquots Created
              </Typography>
              <Box sx={flexBTW}>
                {dashboardData?.aliquot_samples ? (
                  <Typography variant="h4">
                    {dashboardData?.aliquot_samples}
                  </Typography>
                ) : (
                  <Typography variant="caption">No Record</Typography>
                )}
                <SwapHorizontalCircleOutlined fontSize="large" />
              </Box>
            </Box>
          </Box>

          {/* Chart */}
          <Box sx={chartContainer}>
            {/* First row of charts */}
            <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
              <Paper elevation={6} sx={chartTiles}>
                <Typography
                  variant="h6"
                  sx={{ margin: "16px 0px 0px 16px", lineHeight: "1" }}
                >
                  {chartConfigs[0].title}
                </Typography>
                {chartConfigs[0].data.length === 0 ? (
                  <Box
                    sx={{
                      height: "180px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        color: "#888",
                        textAlign: "center",
                      }}
                    >
                      No data to display
                    </Typography>
                  </Box>
                ) : (
                  <Chart
                    chartData={chartConfigs[0].data}
                    colors={chartConfigs[0].colors}
                    ishidden={false}
                  />
                )}
              </Paper>
              <Paper elevation={6} sx={chartTiles}>
                <Typography
                  variant="h6"
                  sx={{ margin: "16px 0px 0px 16px", lineHeight: "1" }}
                >
                  {chartConfigs[1].title}
                </Typography>
                <Chart
                  chartData={chartConfigs[1].data}
                  colors={chartConfigs[1].colors}
                  ishidden={true}
                />
              </Paper>
            </Box>

            {/* Second row of charts */}
            <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
              <Paper elevation={6} sx={chartTiles}>
                <Box sx={flexBTW}>
                  <Typography
                    variant="h6"
                    sx={{ margin: "16px 0px 0px 16px", lineHeight: "1" }}
                  >
                    {chartConfigs[2].title}
                  </Typography>
                </Box>
                <Chart
                  chartData={chartConfigs[2].data}
                  colors={chartConfigs[2].colors}
                  ishidden={false}
                />
              </Paper>
              <Paper elevation={6} sx={chartTiles}>
                <Box sx={flexBTW}>
                  <Typography
                    variant="h6"
                    sx={{ margin: "16px 0px 0px 16px", lineHeight: "1" }}
                  >
                    Samples Wise Site
                  </Typography>
                  <FormControl
                    size="small"
                    sx={{ minWidth: 150, mr: 2, mt: 2 }}
                  >
                    <Autocomplete
                      id="site-autocomplete"
                      size="small"
                      options={
                        Array.isArray(dashboardData.site_counts)
                          ? dashboardData.site_counts.map((site) => site.site)
                          : []
                      }
                      value={siteState || null}
                      onChange={(e, newValue) => setSiteState(newValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Site"
                          size="small"
                          placeholder="Select Site"
                          type="text"
                          name={"Site"}
                          aria-label={"Select Site"}
                        />
                      )}
                      sx={{ width: 180 }}
                      isOptionEqualToValue={(option, value) => option === value}
                      getOptionLabel={(option) =>
                        option ? option.toString() : ""
                      }
                    />
                  </FormControl>
                </Box>
                <SiteBarChart
                  data={
                    siteState
                      ? dashboardData.site_counts.filter(
                          (site) => site.site === siteState
                        )
                      : dashboardData.site_counts
                  }
                />
              </Paper>
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <Dialog
          open={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
        >
          <Box sx={{ p: 3 }}>
            <Typography id="confirm-dialog-title" variant="h6" sx={{ mb: 2 }}>
              Confirm Selection
            </Typography>
            <Typography id="confirm-dialog-description" sx={{ mb: 3 }}>
              Are you sure you want to set default selected Client and
              Project(s)
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setShowConfirmModal(false)}
              >
                No
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSetDefault}
              >
                Yes
              </Button>
            </Box>
          </Box>
        </Dialog>
      )}
    </Box>
  );
};

export default Dashboard;
